import { GlassPanel } from '@/components/brand/glass-panel';
import { SocialConnections } from '@/components/social-connections';
import { Button } from '@sixam/ui/button';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Separator } from '@sixam/ui/separator';
import { TERMS_SUMMARY } from '@sixam/shared/community-terms';
import { BRAND } from '@sixam/config/brand';
import { Text } from '@sixam/ui/text';
import { getEmailFieldError } from '@sixam/supabase/auth';
import { supabase } from '@sixam/supabase/client';
import { Icon } from '@sixam/ui/icon';
import { router } from 'expo-router';
import { useAnalytics } from '@/components/analytics-provider';
import { CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, type TextInput, View } from 'react-native';
import { authDestination, signUpHref, validatedInternalReturnPath } from '@/lib/auth-return';
import { recordTermsAcceptance } from '@/lib/community-terms-client';

export function SignInForm({ returnTo }: Readonly<{ returnTo?: string | string[] }>) {
  const posthog = useAnalytics();
  const passwordInputRef = React.useRef<TextInput>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [error, setError] = React.useState<{
    email?: string;
    password?: string;
    terms?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  function onEmailSubmitEditing() {
    passwordInputRef.current?.focus();
  }

  async function onSubmit() {
    if (isSubmitting) {
      return;
    }

    if (!termsAccepted) {
      setError({ terms: 'Please agree to the community terms before signing in.' });
      return;
    }

    setIsSubmitting(true);
    setError({});

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError(getEmailFieldError(signInError.message));
      } else {
        try {
          await recordTermsAcceptance();
        } catch (acceptanceError) {
          await supabase.auth.signOut({ scope: 'local' });
          throw acceptanceError;
        }
        posthog.capture('signed_in', { method: 'password' });
        router.replace(authDestination(returnTo));
      }
    } catch (submitError) {
      setError({
        password: submitError instanceof Error ? submitError.message : 'Could not sign in.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <View>
      <GlassPanel className="rounded-xl" contentClassName="gap-7 px-5 py-6 sm:px-6 sm:py-7">
        <View className="gap-2">
          <Text className="font-sans text-xs font-bold uppercase leading-none text-white/70">
            Member access
          </Text>
          <Text
            variant="h2"
            className="normal-case leading-none text-white"
            style={{ fontSize: 30 }}
          >
            Sign in
          </Text>
          <Text className="max-w-[300px] text-sm leading-5 text-white/80">
            Check in, find the next run, and keep your 6am BKK profile ready.
          </Text>
        </View>
        <View className="gap-6">
          <View className="gap-6">
            <View className="gap-1.5">
              <Label
                className="font-sans text-xs font-bold uppercase text-white/75"
                htmlFor="email"
              >
                Email
              </Label>
              <Input
                id="email"
                placeholder="runner@6ambkk.com"
                keyboardType="email-address"
                autoComplete="email"
                autoCapitalize="none"
                value={email}
                onChangeText={setEmail}
                onSubmitEditing={onEmailSubmitEditing}
                returnKeyType="next"
                submitBehavior="submit"
              />
              {error.email ? (
                <Text className="text-sm text-destructive" selectable>
                  {error.email}
                </Text>
              ) : null}
            </View>
            <View className="gap-1.5">
              <Label
                className="font-sans text-xs font-bold uppercase text-white/75"
                htmlFor="password"
              >
                Password
              </Label>
              <View className="relative">
                <Input
                  ref={passwordInputRef}
                  id="password"
                  autoCapitalize="none"
                  autoComplete="current-password"
                  className="pr-14"
                  secureTextEntry={!isPasswordVisible}
                  textContentType="password"
                  value={password}
                  onChangeText={setPassword}
                  returnKeyType="send"
                  onSubmitEditing={onSubmit}
                />
                <Pressable
                  accessibilityLabel={isPasswordVisible ? 'Hide password' : 'Show password'}
                  accessibilityRole="button"
                  className="absolute right-0 top-0 h-12 w-12 items-center justify-center rounded-r-xl active:bg-white/10 sm:h-11 sm:w-11"
                  hitSlop={2}
                  onPress={() => setIsPasswordVisible((visible) => !visible)}
                >
                  <Icon
                    as={isPasswordVisible ? EyeOffIcon : EyeIcon}
                    className="size-5 text-white/75"
                  />
                </Pressable>
              </View>
              <Button
                variant="link"
                size="sm"
                className="h-6 self-start px-0 py-0 web:h-fit sm:h-6"
                onPress={() => router.push('/(public)/forgot-password')}
              >
                <Text className="font-sans text-[10px] font-bold uppercase leading-none tracking-[2px]">
                  Forgot password?
                </Text>
              </Button>
              {error.password ? (
                <Text className="text-sm text-destructive" selectable>
                  {error.password}
                </Text>
              ) : null}
            </View>
            <Pressable
              accessibilityHint="Required before signing in"
              accessibilityLabel="I agree to the community terms"
              accessibilityRole="checkbox"
              accessibilityState={{ checked: termsAccepted, disabled: isSubmitting }}
              className="flex-row items-start gap-3 rounded-xl border border-white/25 bg-black/35 p-4 active:opacity-80"
              disabled={isSubmitting}
              onPress={() => setTermsAccepted((current) => !current)}
            >
              <View
                className="mt-0.5 h-5 w-5 items-center justify-center rounded border border-primary"
                style={{ backgroundColor: termsAccepted ? BRAND.color.orange : 'transparent' }}
              >
                {termsAccepted ? <CheckIcon color="white" size={14} /> : null}
              </View>
              <View className="min-w-0 flex-1 gap-1">
                <Text className="font-sans text-xs font-bold uppercase tracking-widest text-white">
                  Community terms
                </Text>
                <Text className="text-sm leading-5 text-white">
                  I agree to the community terms. {TERMS_SUMMARY}
                </Text>
              </View>
            </Pressable>
            <Pressable
              accessibilityRole="link"
              className="active:opacity-70"
              onPress={() => router.push('/terms')}
            >
              <Text className="font-sans text-xs font-bold uppercase tracking-widest text-primary underline underline-offset-4">
                Read the full terms
              </Text>
            </Pressable>
            {error.terms ? (
              <Text className="text-sm text-destructive" selectable>
                {error.terms}
              </Text>
            ) : null}
            <Button
              className="h-14 w-full"
              onPress={onSubmit}
              disabled={isSubmitting || !termsAccepted}
            >
              <Text>{isSubmitting ? 'Signing in...' : 'Sign in'}</Text>
            </Button>
          </View>
          <Text className="text-center text-sm text-white/75">
            Need a member profile?{' '}
            <Pressable
              onPress={() => router.push(signUpHref(validatedInternalReturnPath(returnTo)))}
            >
              <Text className="font-sans text-xs font-bold uppercase tracking-widest text-primary underline underline-offset-4">
                Sign up
              </Text>
            </Pressable>
          </Text>
          <View className="flex-row items-center">
            <Separator className="bg-white/18 flex-1" />
            <Text className="px-4 font-mono text-xs uppercase text-white/60">or</Text>
            <Separator className="bg-white/18 flex-1" />
          </View>
          <SocialConnections returnTo={returnTo} termsAccepted={termsAccepted} />
        </View>
      </GlassPanel>
    </View>
  );
}
